// src/documents/exportInventory.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import grupoLogo from '../assets/grupo_logo.png';

/**
 * Loads and adds the company logo to the workbook
 */
const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }

        const blob = await logoResponse.blob();
        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}".`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        return workbook.addImage({ base64, extension: ext });
    } catch (error) {
        console.error('Logo loading error:', error);
        return null;
    }
};

/**
 * Creates a formatted inventory report sheet
 */
const createInventorySheet = (workbook, logoImageId, { items, filters, reportTitle }) => {
    const worksheet = workbook.addWorksheet('Relatório de Estoque');

    // Set column widths
    worksheet.getColumn(1).width = 45;  // Código
    worksheet.getColumn(2).width = 45;  // Produto
    worksheet.getColumn(3).width = 30;  // Localização
    worksheet.getColumn(4).width = 30;  // QTD

    let currentRow = 1;

    // ========================================================================
    // HEADER SECTION (Rows 1-2)
    // ========================================================================
    worksheet.getRow(1).height = 38;
    worksheet.getRow(2).height = 38;

    // Logo (Cells A1:A2)
    worksheet.mergeCells('A1:A2');
    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.5, row: 0.2 },
            ext: { width: 230, height: 85 },
            editAs: 'oneCell'
        });
    }

    // Title merged on B1:C2 (both rows, columns B and C)
    worksheet.mergeCells('B1:C2');
    const titleCell = worksheet.getCell('B1');
    titleCell.value = reportTitle || 'RELATÓRIO DE ESTOQUE';
    titleCell.font = { bold: true, size: 14, name: 'Arial' };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    // Current Date - Cell D1
    const dateCell = worksheet.getCell('D1');
    dateCell.value = `Data: ${new Date().toLocaleDateString('pt-BR')}`;
    dateCell.font = { bold: true, size: 11, name: 'Arial' };
    dateCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // FQ Code - Cell D2
    const fqCell = worksheet.getCell('D2');
    fqCell.value = 'FQ 41\nRev. 00';
    fqCell.font = { bold: true, size: 11, name: 'Arial' };
    fqCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    // Apply header borders
    const colCount = 4;
    for (let row = 1; row <= 2; row++) {
        for (let c = 1; c <= colCount; c++) {
            const cell = worksheet.getCell(row, c);
            cell.border = {
                top: { style: row === 1 ? 'medium' : 'thin' },
                left: { style: c === 1 ? 'medium' : 'thin' },
                bottom: { style: row === 2 ? 'medium' : 'thin' },
                right: { style: c === colCount ? 'medium' : 'thin' }
            };
        }
    }

    currentRow = 3;

    // ========================================================================
    // TABLE HEADERS (Row 3)
    // ========================================================================
    const headers = ['Código', 'Produto', 'Localização', 'QTD'];

    headers.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11, name: 'Arial' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
            top: { style: 'medium' },
            left: { style: i === 0 ? 'medium' : 'thin' },
            bottom: { style: 'medium' },
            right: { style: i === headers.length - 1 ? 'medium' : 'thin' }
        };
    });
    worksheet.getRow(currentRow).height = 30;
    currentRow++;

    // ========================================================================
    // TABLE DATA
    // ========================================================================
    if (items && items.length > 0) {
        items.forEach(item => {
            // Código
            const codeCell = worksheet.getCell(`A${currentRow}`);
            codeCell.value = item.code;
            codeCell.font = { size: 10, name: 'Arial' };
            codeCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // Produto
            const productCell = worksheet.getCell(`B${currentRow}`);
            productCell.value = item.name;
            productCell.font = { size: 10, name: 'Arial' };
            productCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

            // Localização
            const locationCell = worksheet.getCell(`C${currentRow}`);
            locationCell.value = item.locations;
            locationCell.font = { size: 10, name: 'Arial' };
            locationCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

            // QTD
            const quantityCell = worksheet.getCell(`D${currentRow}`);
            quantityCell.value = item.quantity;
            quantityCell.font = { size: 10, name: 'Arial' };
            quantityCell.alignment = { horizontal: 'center', vertical: 'middle' };
            quantityCell.numFmt = '#,##0.000';

            // Apply borders
            for (let c = 1; c <= colCount; c++) {
                const cell = worksheet.getCell(currentRow, c);
                cell.border = {
                    top: { style: 'thin' },
                    left: { style: c === 1 ? 'medium' : 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: c === colCount ? 'medium' : 'thin' }
                };
            }
            worksheet.getRow(currentRow).height = 35;
            currentRow++;
        });
    }

    // ========================================================================
    // FOOTER SECTION (Signature lines)
    // ========================================================================
    currentRow = currentRow + 1;
    worksheet.getRow(currentRow).height = 50;

    // Elaborado por
    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    const elaboratedCell = worksheet.getCell(`A${currentRow}`);
    elaboratedCell.value = 'Elaborado por: Logística\nData: 21/05/2026';
    elaboratedCell.font = { size: 9, name: 'Arial' };
    elaboratedCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    // Revisado por
    worksheet.mergeCells(`C${currentRow}:C${currentRow}`);
    const reviewedCell = worksheet.getCell(`C${currentRow}`);
    reviewedCell.value = 'Revisado por: Magali Murro\nData: 21/05/2026';
    reviewedCell.font = { size: 9, name: 'Arial' };
    reviewedCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    // Aprovado por
    worksheet.mergeCells(`D${currentRow}:D${currentRow}`);
    const approvedCell = worksheet.getCell(`D${currentRow}`);
    approvedCell.value = 'Aprovado por: Nisrine El Hazzat\nData: 21/05/2026';
    approvedCell.font = { size: 9, name: 'Arial' };
    approvedCell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= colCount; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' },
            left: { style: c === 1 ? 'thin' : 'thin' },
            bottom: { style: 'thin' },
            right: { style: c === colCount ? 'thin' : 'thin' }
        };
    }

    // ========================================================================
    // PRINT SETUP - Repeat header and footer on each page
    // ========================================================================

    // Set rows 1-3 to repeat at top of each page (header)
    worksheet.pageSetup.printTitlesRow = '1:3';

    // Set footer rows to repeat at bottom of each page
    // Note: ExcelJS doesn't support repeating footer rows directly,
    // so we'll use print area and ensure the footer appears on each page
    // by keeping it on the last row of the print area

    // Set print area from row 1 to currentRow (includes footer)
    worksheet.pageSetup.printArea = `A1:D${currentRow}`;

    // Page orientation and scaling
    worksheet.pageSetup.orientation = 'landscape';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.paperSize = 9; // A4

    // Add page numbers to footer
    worksheet.headerFooter.oddFooter = '&C&Página &P de &N';
    worksheet.headerFooter.evenFooter = '&C&Página &P de &N';
};

/**
 * Exports inventory report with professional formatting
 * 
 * @param {Object} params - Export parameters
 * @param {Array} params.items - Array of inventory items
 *   Each item: { code, name, quantity, locations, locationDetails }
 * @param {Object} params.filters - Applied filters (productTypeName, categoryName, etc.)
 * @param {string} params.reportTitle - Custom report title
 * @param {string} params.fileName - File name for download
 * @param {Function} params.showNotification - Callback for status messages
 */
export const exportInventory = async ({ items, filters, reportTitle, fileName, showNotification }) => {
    try {
        if (!items) {
            throw new Error('Items array is required');
        }

        const workbook = new ExcelJS.Workbook();
        const logoImageId = await loadLogo(workbook);

        createInventorySheet(workbook, logoImageId, {
            items,
            filters: filters || {},
            reportTitle: reportTitle || 'RELATÓRIO DE ESTOQUE'
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        saveAs(blob, fileName);

        if (showNotification) {
            showNotification('Relatório gerado com sucesso!', 'success');
        }
    } catch (error) {
        console.error("Erro ao gerar relatório:", error);
        if (showNotification) {
            showNotification(`Erro ao gerar relatório: ${error.message}`, 'error');
        }
        throw error;
    }
};